import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import { useApi } from "../../utils/api";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import LoadingButton from "@mui/lab/LoadingButton";
import { useUserInfo } from "../../utils/user";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Roboto Mono, monospace",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const BuyProductsModal = ({
  open,
  onClose,
  assignedProducts,
  onSuccess,
  paymentMethodId,
  stripeCustomerId,
  hasSavedPaymentMethod,
  userEmail,
}) => {
  const { coachAccountId, id: userId } = useUserInfo();
  const { createOneTimePaymentIntent, postUserProducts, getPaymentMethods } =
    useApi();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(false);
  const [error, setError] = useState();
  const stripe = useStripe();
  const elements = useElements();
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  useEffect(() => {
    const loadPaymentMethod = async () => {
      if (!open || !hasSavedPaymentMethod || !stripeCustomerId) {
        setDefaultPaymentMethod(null);
        return;
      }
      try {
        setLoadingPaymentMethod(true);
        const resp = await getPaymentMethods(stripeCustomerId, coachAccountId);
        const defaultId = resp?.defaultPaymentMethodId;
        const match =
          resp?.paymentMethods?.find((pm) => pm.id === defaultId) ||
          resp?.paymentMethods?.[0] ||
          null;
        setDefaultPaymentMethod(match);
      } catch (err) {
        setDefaultPaymentMethod(null);
      } finally {
        setLoadingPaymentMethod(false);
      }
    };
    loadPaymentMethod();

    // Reset card state when modal closes
    if (!open) {
      setCardComplete(false);
      setError(undefined);
    }
  }, [
    open,
    hasSavedPaymentMethod,
    stripeCustomerId,
    coachAccountId,
    getPaymentMethods,
  ]);

  // Auto-select product if there's only one, and reset when modal closes
  useEffect(() => {
    if (!open) {
      // Reset selection when modal closes
      setSelectedProducts([]);
    } else if (assignedProducts.length === 1) {
      // Auto-select if there's only one product
      setSelectedProducts([assignedProducts[0]]);
    }
  }, [open, assignedProducts]);

  const effectivePaymentMethodId =
    defaultPaymentMethod?.id || paymentMethodId || null;

  const onProductSelect = (product) => {
    setSelectedProducts((prev) => {
      const isSelected = prev.some((p) => p.priceId === product.priceId);
      if (isSelected) {
        return prev.filter((p) => p.priceId !== product.priceId);
      }
      return [...prev, product];
    });
  };

  const handleBuyProducts = async () => {
    setLoading(true);
    setError(undefined);

    try {
      if (!stripe) throw new Error("Stripe not ready");

      await Promise.all(
        selectedProducts.map(async (product) => {
          // 1) Create PI server-side (correct account context)
          const resp = await createOneTimePaymentIntent({
            stripeCustomerId,
            oneTimePriceId: product.priceId,
            stripeAccountId: coachAccountId,
          });

          if (!resp?.client_secret) {
            throw new Error("Failed to create payment intent");
          }

          // 2) Confirm client-side
          let confirmParams = {};
          if (effectivePaymentMethodId) {
            confirmParams = { payment_method: effectivePaymentMethodId };
          } else {
            const card = elements?.getElement(CardElement);
            if (!card) {
              throw new Error("Card input not ready");
            }
            confirmParams = {
              payment_method: {
                card,
                billing_details: {
                  email: userEmail || undefined,
                },
              },
            };
          }

          const { error: piError, paymentIntent } =
            await stripe.confirmCardPayment(resp.client_secret, confirmParams);

          if (piError) {
            throw new Error(piError.message);
          }
          if (paymentIntent?.status !== "succeeded") {
            throw new Error("Payment did not succeed.");
          }

          // 3) Record purchase
          await postUserProducts({
            items: [{ productId: product.productId, priceId: product.priceId }],
            type: "purchased",
            userId,
            stripeAccountId: coachAccountId,
          });
        })
      );

      onSuccess();
    } catch (err) {
      setError(err.message || "Error submitting payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Products</DialogTitle>
      {error && (
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}
      <DialogContent>
        <Grid container spacing={2}>
          {assignedProducts.map((product) => (
            <Grid item xs={12} sm={6} key={product.priceId}>
              <Paper
                sx={{
                  p: 2,
                  cursor: "pointer",
                  border: selectedProducts.some(
                    (p) => p.priceId === product.priceId
                  )
                    ? "2px solid #ff0000"
                    : "none",
                }}
                onClick={() => onProductSelect(product)}
              >
                <Typography variant="h6">{product.productName}</Typography>
                <Typography variant="body2">
                  {product.priceDescription}
                </Typography>
                <Typography variant="h5">
                  ${product.price.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.quantityLeft} session
                  {product.quantityLeft === 1 ? "" : "s"}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {selectedProducts.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Selected Products</Typography>
            {selectedProducts.map((product) => (
              <Box key={product.priceId} sx={{ mt: 1 }}>
                <Typography variant="body1">
                  {product.productName} - ${product.price.toFixed(2)}
                </Typography>
              </Box>
            ))}
            <Typography variant="h5" sx={{ mt: 2 }}>
              Total: $
              {selectedProducts
                .reduce((sum, product) => sum + product.price, 0)
                .toFixed(2)}
            </Typography>
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle1">Payment method</Typography>
              {loadingPaymentMethod && (
                <Typography variant="body2" color="text.secondary">
                  Loading payment method...
                </Typography>
              )}
              {!loadingPaymentMethod && effectivePaymentMethodId && (
                <Typography variant="body2">
                  {defaultPaymentMethod?.card?.brand
                    ? defaultPaymentMethod.card.brand.toUpperCase()
                    : "Card"}{" "}
                  •••• {defaultPaymentMethod?.card?.last4 || ""}
                  {defaultPaymentMethod?.card?.exp_month &&
                    defaultPaymentMethod?.card?.exp_year &&
                    ` (${defaultPaymentMethod.card.exp_month}/${defaultPaymentMethod.card.exp_year})`}
                </Typography>
              )}
              {!loadingPaymentMethod && !effectivePaymentMethodId && open && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Enter payment details
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <CardElement
                      options={CARD_ELEMENT_OPTIONS}
                      onChange={(e) => setCardComplete(e.complete)}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {selectedProducts.length > 0 && (
          <LoadingButton
            onClick={handleBuyProducts}
            disabled={
              !stripe ||
              loading ||
              loadingPaymentMethod ||
              (!effectivePaymentMethodId && !cardComplete)
            }
            loading={loading}
          >
            Buy Now
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BuyProductsModal;
